import { BoundingBox, Vec3 } from 'playcanvas';

import { Camera } from './camera';
import { applyProximityPushbackWithColliders, constrainToBox, getBlockingSceneColliders, raycastGroundHeight, resolveSolidCollision } from './collision';

const fromWorldPoint = new Vec3();
const toWorldPoint = new Vec3();
const worldDiff = new Vec3();
const moveVec = new Vec3();
const forwardVec = new Vec3();
const camPos0 = new Vec3();
const camPos1 = new Vec3();
const focalPoint = new Vec3();

const boundsBox = new BoundingBox();

// Build a padded bounding box from the scene bound for camera containment.
const getPaddedBounds = (camera: Camera): BoundingBox | null => {
    const events = camera.scene.events;
    if (!events.invoke('camera.boundsEnabled')) return null;
    const sceneBound = camera.scene.bound;
    const padding = (events.invoke('camera.boundsPadding') as number) ?? 1.5;
    boundsBox.center.copy(sceneBound.center);
    boundsBox.halfExtents.copy(sceneBound.halfExtents).mulScalar(padding);
    return boundsBox;
};

// calculate the distance between two 2d points
const dist = (x0: number, y0: number, x1: number, y1: number) => Math.sqrt((x1 - x0) ** 2 + (y1 - y0) ** 2);

class PointerController {
    update: (deltaTime: number) => void;
    destroy: () => void;

    constructor(camera: Camera, target: HTMLElement) {

        // Orbit mode: rotate camera around the focal point
        const orbit = (dx: number, dy: number) => {
            const azim = camera.azim - dx * camera.scene.config.controls.orbitSensitivity;
            const elev = camera.elevation - dy * camera.scene.config.controls.orbitSensitivity;
            camera.setAzimElev(azim, elev);
        };

        // Fly mode: rotate camera around itself (keep camera position fixed)
        const look = (dx: number, dy: number) => {
            // Use TARGET values to calculate target camera position (not current interpolated)
            const distance = camera.distance * camera.sceneRadius / camera.fovFactor;

            // Calculate target camera position from target focal point and angles
            Camera.calcForwardVec(forwardVec, camera.azim, camera.elevation);
            const targetCameraPos = camera.focalPoint.add(forwardVec.clone().mulScalar(distance));

            // Calculate new azim/elev
            const azim = camera.azim - dx * camera.scene.config.controls.orbitSensitivity;
            const elev = camera.elevation - dy * camera.scene.config.controls.orbitSensitivity;

            // Calculate the new forward vector based on new angles
            Camera.calcForwardVec(forwardVec, azim, elev);

            // Calculate new focal point to keep camera at target position
            // Camera position = focalPoint + forwardVec * distance
            // So: focalPoint = cameraPosition - forwardVec * distance
            const newFocalPoint = targetCameraPos.clone().sub(forwardVec.clone().mulScalar(distance));

            camera.setAzimElev(azim, elev);
            camera.setFocalPoint(newFocalPoint);
        };

        const pan = (x: number, y: number, dx: number, dy: number) => {
            // For panning to work at any zoom level, we use screen point to world projection
            // to work out how far we need to pan the pivotEntity in world space
            const c = camera.camera;
            const distance = camera.distanceTween.value.distance * camera.sceneRadius / camera.fovFactor;

            c.screenToWorld(x, y, distance, fromWorldPoint);
            c.screenToWorld(x - dx, y - dy, distance, toWorldPoint);

            worldDiff.sub2(toWorldPoint, fromWorldPoint);
            worldDiff.add(camera.focalPoint);

            camera.setFocalPoint(worldDiff);
        };

        const zoom = (amount: number) => {
            camera.setDistance(camera.distance - (camera.distance * 0.999 + 0.001) * amount * camera.scene.config.controls.zoomSensitivity, 2);
        };

        // mouse state
        let pressedButton = -1;  // no button pressed, otherwise 0, 1, or 2
        let x: number, y: number;

        // touch state
        let touches: { id: number, x: number, y: number}[] = [];
        let midx: number, midy: number, midlen: number;

        const pointerdown = (event: PointerEvent) => {
            if (event.pointerType === 'mouse') {
                // If a button is already pressed, ignore this press
                if (pressedButton !== -1) {
                    return;
                }
                target.setPointerCapture(event.pointerId);
                pressedButton = event.button;
                x = event.offsetX;
                y = event.offsetY;
            } else if (event.pointerType === 'touch') {
                if (touches.length === 0) {
                    target.setPointerCapture(event.pointerId);
                }
                touches.push({
                    x: event.offsetX,
                    y: event.offsetY,
                    id: event.pointerId
                });

                if (touches.length === 2) {
                    midx = (touches[0].x + touches[1].x) * 0.5;
                    midy = (touches[0].y + touches[1].y) * 0.5;
                    midlen = dist(touches[0].x, touches[0].y, touches[1].x, touches[1].y);
                }
            }
        };

        const pointerup = (event: PointerEvent) => {
            if (event.pointerType === 'mouse') {
                // Only release if this is the button that was initially pressed
                if (event.button === pressedButton) {
                    pressedButton = -1;
                    target.releasePointerCapture(event.pointerId);
                }
            } else {
                touches = touches.filter(touch => touch.id !== event.pointerId);
                if (touches.length === 0) {
                    target.releasePointerCapture(event.pointerId);
                }
            }
        };

        const pointermove = (event: PointerEvent) => {
            if (event.pointerType === 'mouse') {
                // Only process if we're tracking a button
                if (pressedButton === -1) {
                    return;
                }

                // Verify the button we're tracking is still pressed
                // 1 = left button, 4 = middle button, 2 = right button
                const buttonMask = [1, 4, 2][pressedButton];
                if ((event.buttons & buttonMask) === 0) {
                    // Button is no longer pressed, clean up
                    pressedButton = -1;
                    return;
                }

                const dx = event.offsetX - x;
                const dy = event.offsetY - y;
                x = event.offsetX;
                y = event.offsetY;

                if (camera.controlMode === 'fly' || camera.controlMode === 'walk') {
                    // Fly mode: left-drag to look around, middle to zoom, right works same as orbit
                    if (pressedButton === 0) {
                        look(dx, dy);
                    } else if (pressedButton === 1) {
                        zoom(dy * -0.02);
                    } else if (pressedButton === 2) {
                        // Right button: same behavior as orbit mode
                        const mod = event.shiftKey || event.ctrlKey ? 'look' :
                            (event.altKey || event.metaKey ? 'zoom' : 'pan');

                        if (mod === 'look') {
                            look(dx, dy);
                        } else if (mod === 'zoom') {
                            zoom(dy * -0.02);
                        } else {
                            pan(x, y, dx, dy);
                        }
                    }
                } else {
                    // Orbit mode: existing behavior
                    // right button can be used to orbit with ctrl key and to zoom with alt | meta key
                    const mod = pressedButton === 2 ?
                        (event.shiftKey || event.ctrlKey ? 'orbit' :
                            (event.altKey || event.metaKey ? 'zoom' : null)) :
                        null;

                    if (mod === 'orbit' || (mod === null && pressedButton === 0)) {
                        orbit(dx, dy);
                    } else if (mod === 'zoom' || (mod === null && pressedButton === 1)) {
                        zoom(dy * -0.02);
                    } else if (mod === 'pan' || (mod === null && pressedButton === 2)) {
                        pan(x, y, dx, dy);
                    }
                }
            } else {
                if (touches.length === 1) {
                    const touch = touches[0];
                    const dx = event.offsetX - touch.x;
                    const dy = event.offsetY - touch.y;
                    touch.x = event.offsetX;
                    touch.y = event.offsetY;

                    if (camera.controlMode === 'fly' || camera.controlMode === 'walk') {
                        look(dx, dy);
                    } else {
                        orbit(dx, dy);
                    }
                } else if (touches.length === 2) {
                    const touch = touches[touches.map(t => t.id).indexOf(event.pointerId)];
                    touch.x = event.offsetX;
                    touch.y = event.offsetY;

                    const mx = (touches[0].x + touches[1].x) * 0.5;
                    const my = (touches[0].y + touches[1].y) * 0.5;
                    const ml = dist(touches[0].x, touches[0].y, touches[1].x, touches[1].y);

                    if (camera.controlMode === 'fly' || camera.controlMode === 'walk') {
                        // In fly mode, pinch moves forward/backward by moving focal point
                        const zoomDelta = (ml - midlen) * 0.01;
                        const worldTransform = camera.mainCamera.getWorldTransform();
                        const zAxis = worldTransform.getZ();
                        const speed = (camera.controlMode === 'walk') ? camera.walkSpeed : camera.flySpeed;
                        moveVec.copy(zAxis).mulScalar(-zoomDelta * speed);
                        const p = camera.focalPoint.add(moveVec);
                        camera.setFocalPoint(p);
                    } else {
                        pan(mx, my, (mx - midx), (my - midy));
                        zoom((ml - midlen) * 0.01);
                    }

                    midx = mx;
                    midy = my;
                    midlen = ml;
                }
            }
        };

        // fuzzy detection of mouse wheel events vs trackpad events
        const isMouseEvent = (deltaX: number, deltaY: number) => {
            return (Math.abs(deltaX) > 50 && deltaY === 0) ||
                   (Math.abs(deltaY) > 50 && deltaX === 0) ||
                   (deltaX === 0 && deltaY !== 0) && !Number.isInteger(deltaY);
        };

        const wheel = (event: WheelEvent) => {
            const { deltaX, deltaY } = event;

            if (camera.controlMode === 'fly' || camera.controlMode === 'walk') {
                // Fly mode: wheel moves forward/backward by moving focal point
                const speed = (camera.controlMode === 'walk') ? camera.walkSpeed : camera.flySpeed;
                const factor = speed * 0.01;
                const worldTransform = camera.mainCamera.getWorldTransform();
                const zAxis = worldTransform.getZ();
                moveVec.copy(zAxis).mulScalar(deltaY * factor);
                const p = camera.focalPoint.add(moveVec);
                camera.setFocalPoint(p);
            } else {
                // Orbit mode: existing behavior
                if (isMouseEvent(deltaX, deltaY)) {
                    zoom(deltaY * -0.002);
                } else if (event.ctrlKey || event.metaKey) {
                    zoom(deltaY * -0.02);
                } else if (event.shiftKey) {
                    pan(event.offsetX, event.offsetY, deltaX, deltaY);
                } else {
                    orbit(deltaX, deltaY);
                }
            }

            event.preventDefault();
        };

        // FIXME: safari sends canvas as target of dblclick event but chrome sends the target element
        const canvas = camera.scene.app.graphicsDevice.canvas;

        // UI chrome inside #canvas-container should not trigger camera focusing.
        // Keep this list narrow and ID-based to avoid accidentally blocking canvas overlays.
        const uiChromeSelector = [
            '#scene-panel',
            '#view-panel',
            '#color-panel',
            '#case-panel',
            '#evidence-panel',
            '#annotation-panel',
            '#bottom-toolbar',
            '#right-toolbar',
            '#mode-toggle',
            '#menu',
            '#timeline-panel',
            '#data-panel',
            '#status-bar',
            '#tooltips-container',
            '#top-container',
            '#popup',
            '#progress-container',
            '#spinner-container',
            'input',
            'textarea',
            'select',
            'button',
            'a'
        ].join(',');

        const dblclick = (event: globalThis.MouseEvent) => {
            const eventTarget = event.target as Node | null;

            // Accept dblclick events whose target is the container itself, the canvas, or any overlay within.
            if (!eventTarget || !(eventTarget === target || eventTarget === canvas || target.contains(eventTarget))) {
                return;
            }

            // Avoid UI chrome (panels/toolbars/inputs) inside the canvas container.
            const el = (eventTarget as any)?.closest ? (eventTarget as unknown as Element) : null;
            if (el?.closest(uiChromeSelector)) {
                return;
            }

            // Compute normalized coords from clientX/clientY against the container rect.
            // Do not use offsetX/offsetY because event.target may be an overlay element.
            const rect = target.getBoundingClientRect();
            const w = rect.width || 0;
            const h = rect.height || 0;
            if (w <= 0 || h <= 0) {
                return;
            }

            const nx = (event.clientX - rect.left) / w;
            const ny = (event.clientY - rect.top) / h;

            if (!Number.isFinite(nx) || !Number.isFinite(ny) || nx < 0 || nx > 1 || ny < 0 || ny > 1) {
                return;
            }

            // Switch to orbit mode when double-clicking to focus
            if (camera.controlMode === 'fly' || camera.controlMode === 'walk') {
                camera.scene.events.fire('camera.setControlMode', 'orbit');
            }

            camera.pickFocalPoint(nx, ny);
        };

        // fly movement state (updated via shortcut events)
        let flyForward = false;
        let flyBackward = false;
        let flyLeft = false;
        let flyRight = false;
        let flyDown = false;
        let flyUp = false;

        // track modifier keys for speed control (updated via shortcut events)
        let fastDown = false;
        let slowDown = false;

        // Clear all keys when window loses focus to prevent stuck keys
        const clearAllKeys = () => {
            flyForward = false;
            flyBackward = false;
            flyLeft = false;
            flyRight = false;
            flyDown = false;
            flyUp = false;
            fastDown = false;
            slowDown = false;
        };

        // Helper to switch to fly mode when a fly key is pressed
        const handleFlyKey = (down: boolean) => {
            if (down && camera.controlMode !== 'fly' && camera.controlMode !== 'walk') {
                camera.scene.events.fire('camera.setControlMode', 'fly');
            }
        };

        const applyRawKeyState = (code: string, down: boolean) => {
            switch (code) {
                case 'KeyW': flyForward = down; break;
                case 'KeyS': flyBackward = down; break;
                case 'KeyA': flyLeft = down; break;
                case 'KeyD': flyRight = down; break;
                case 'KeyQ': flyDown = down; break;
                case 'KeyE': flyUp = down; break;
                case 'ShiftLeft':
                case 'ShiftRight':
                    fastDown = down;
                    break;
                case 'AltLeft':
                case 'AltRight':
                    slowDown = down;
                    break;
            }
        };

        const rawKeyDown = (event: KeyboardEvent) => {
            applyRawKeyState(event.code, true);
        };

        const rawKeyUp = (event: KeyboardEvent) => {
            applyRawKeyState(event.code, false);
        };

        // Listen for fly movement shortcut events
        const events = camera.scene.events;

        const onFlyForward = (down: boolean) => {
            flyForward = down;
            handleFlyKey(down);
        };
        const onFlyBackward = (down: boolean) => {
            flyBackward = down;
            handleFlyKey(down);
        };
        const onFlyLeft = (down: boolean) => {
            flyLeft = down;
            handleFlyKey(down);
        };
        const onFlyRight = (down: boolean) => {
            flyRight = down;
            handleFlyKey(down);
        };
        const onFlyDown = (down: boolean) => {
            flyDown = down;
            handleFlyKey(down);
        };
        const onFlyUp = (down: boolean) => {
            flyUp = down;
            handleFlyKey(down);
        };
        const onModifierFast = (down: boolean) => {
            fastDown = down;
        };
        const onModifierSlow = (down: boolean) => {
            slowDown = down;
        };

        // Walk idle snap caching: if the implied camera XZ doesn't change between frames,
        // avoid repeated raycasts for ground height.
        let lastIdleSnapX = Number.NaN;
        let lastIdleSnapZ = Number.NaN;
        let lastIdleSnapWalkHeight = Number.NaN;
        let lastIdleSnapEnabled = false;
        let lastIdleGroundY: number | null = null;

        events.on('camera.fly.forward', onFlyForward);
        events.on('camera.fly.backward', onFlyBackward);
        events.on('camera.fly.left', onFlyLeft);
        events.on('camera.fly.right', onFlyRight);
        events.on('camera.fly.down', onFlyDown);
        events.on('camera.fly.up', onFlyUp);
        events.on('camera.modifier.fast', onModifierFast);
        events.on('camera.modifier.slow', onModifierSlow);

        this.update = (deltaTime: number) => {
            if (camera.controlMode !== 'fly' && camera.controlMode !== 'walk') return;

            const isWalk = camera.controlMode === 'walk';

            // Fly mode: WASD for movement, Q/E for up/down - moves focal point
            // Walk mode: WASD only (horizontal), no vertical movement.
            const forward = (flyForward ? 1 : 0) - (flyBackward ? 1 : 0);
            const strafe = (flyRight ? 1 : 0) - (flyLeft ? 1 : 0);
            const vertical = isWalk ? 0 : (flyUp ? 1 : 0) - (flyDown ? 1 : 0);

            const hadMoveInput = !!(forward || strafe || vertical);

            if (hadMoveInput) {
                if (!isWalk) {
                    const collisionSources = (events.invoke('camera.collisionSources') ?? 'none') as string;
                    if (forward > 0 && (collisionSources === 'splats' || collisionSources === 'all')) {
                        const canvas = camera.scene.canvas;
                        camera.intersect(canvas.clientWidth * 0.5, canvas.clientHeight * 0.5, {
                            nearRejectDistance: 0,
                            strictMinSplatOpacity: true,
                            splatPickSize: 1
                        });
                    }
                }

                // Calculate speed modifier based on current modifier key state
                const speedMod = fastDown ? 10 : (slowDown ? 0.1 : 1);
                const speed = isWalk ? camera.walkSpeed : camera.flySpeed;
                const factor = deltaTime * speed * speedMod;
                const worldTransform = camera.worldTransform;

                moveVec.set(0, 0, 0);

                if (!isWalk) {
                    // Fly mode: move along the camera's actual look direction (not flattened).
                    // This gives a natural FPS-style feel where W moves where you're looking.
                    if (forward) {
                        const zAxis = worldTransform.getZ();
                        moveVec.add(zAxis.mulScalar(-forward * factor));
                    }

                    if (strafe) {
                        const xAxis = worldTransform.getX();
                        moveVec.add(xAxis.mulScalar(strafe * factor));
                    }

                    // Q/E: move in world-space Y
                    if (vertical) {
                        moveVec.y += vertical * factor;
                    }

                    // Move camera position directly, then update focal point to match.
                    // This avoids the indirect focal-point-first approach that causes drift.
                    const distance = camera.distance * camera.sceneRadius / camera.fovFactor;
                    Camera.calcForwardVec(forwardVec, camera.azim, camera.elevation);
                    camPos0.copy(forwardVec).mulScalar(distance).add(camera.focalPoint);
                    camPos1.copy(camPos0).add(moveVec);

                    // Camera bounds: clamp the camera position to the padded scene box.
                    // Clamping the target (not the tween) prevents rubber-banding.
                    const flyBounds = getPaddedBounds(camera);
                    if (flyBounds) {
                        const clamped = constrainToBox(camPos1, flyBounds, 0);
                        camPos1.copy(clamped);
                    }

                    focalPoint.sub2(camPos1, forwardVec.mulScalar(distance));
                    camera.setFocalPoint(focalPoint, 0);
                    return;
                }

                // Walk mode: horizontal-only movement (flattened axes).
                if (forward) {
                    const zAxis = worldTransform.getZ();
                    zAxis.y = 0;
                    zAxis.normalize();
                    moveVec.add(zAxis.mulScalar(-forward * factor));
                }

                if (strafe) {
                    const xAxis = worldTransform.getX();
                    xAxis.y = 0;
                    xAxis.normalize();
                    moveVec.add(xAxis.mulScalar(strafe * factor));
                }

                // Walk mode: move the camera position horizontally, collide against blocking meshes,
                // then update focal point so the camera ends up at the intended position.
                // Use world-space positions for collision + snapping calculations.
                camPos0.copy(camera.mainCamera.getPosition());
                camPos1.copy(camPos0).add(moveVec);

                // Collision: prevent walking through blocking meshes (AABB-based).
                const blockingColliders = getBlockingSceneColliders(camera.scene);
                const resolved = resolveSolidCollision(camera.scene, camPos0, camPos1, 0.2, { colliders: blockingColliders });
                camPos1.copy(resolved);
                const clearance = (events.invoke('camera.collisionDistance') as number | undefined) ?? 0.6;
                camPos1.copy(applyProximityPushbackWithColliders(blockingColliders, camPos0, camPos1, clearance));

                // Ground snapping: raycast down against blocking meshes only.
                if (camera.walkEnabled) {
                    if (camera.walkSnapToMesh) {
                        const groundY = raycastGroundHeight(camera.scene, camPos1.x, camPos1.z);
                        camPos1.y = (groundY !== null) ? (groundY + camera.walkHeight) : camera.walkHeight;
                    } else {
                        camPos1.y = camera.walkHeight;
                    }
                }

                // Camera bounds: clamp camera position to padded scene box.
                const walkBounds = getPaddedBounds(camera);
                if (walkBounds) {
                    const clamped = constrainToBox(camPos1, walkBounds, 0);
                    camPos1.copy(clamped);
                }

                // Convert desired camera position -> focal point.
                const distance = camera.distance * camera.sceneRadius / camera.fovFactor;
                Camera.calcForwardVec(forwardVec, camera.azim, camera.elevation);
                focalPoint.copy(forwardVec).mulScalar(distance);
                focalPoint.sub2(camPos1, focalPoint);
                camera.setFocalPoint(focalPoint, 0);
            }

            // Walk mode: even without movement input, enforce walk height / ground snap.
            // This keeps the camera clamped when users (or tests) adjust pose directly.
            // NOTE: When there is movement input, the movement branch already snaps/clamps, so
            // we avoid doing extra raycasts here.
            if (isWalk && camera.walkEnabled && !hadMoveInput) {
                const distance = camera.distance * camera.sceneRadius / camera.fovFactor;
                Camera.calcForwardVec(forwardVec, camera.azim, camera.elevation);

                // Compute the camera position implied by the *target* pose.
                camPos1.copy(forwardVec).mulScalar(distance);
                camPos1.add(camera.focalPoint);

                let targetY = camera.walkHeight;
                if (camera.walkSnapToMesh) {
                    const eps = 1e-4;
                    const snapX = camPos1.x;
                    const snapZ = camPos1.z;
                    const snapEnabled = true;
                    const sameXZ = Math.abs(snapX - lastIdleSnapX) < eps && Math.abs(snapZ - lastIdleSnapZ) < eps;
                    const sameHeight = Math.abs(camera.walkHeight - lastIdleSnapWalkHeight) < eps;
                    const canReuse = snapEnabled === lastIdleSnapEnabled && sameXZ && sameHeight;

                    const groundY = canReuse ? lastIdleGroundY : raycastGroundHeight(camera.scene, snapX, snapZ);
                    if (!canReuse) {
                        lastIdleSnapX = snapX;
                        lastIdleSnapZ = snapZ;
                        lastIdleSnapWalkHeight = camera.walkHeight;
                        lastIdleSnapEnabled = snapEnabled;
                        lastIdleGroundY = groundY;
                    }

                    targetY = (groundY !== null) ? (groundY + camera.walkHeight) : camera.walkHeight;
                } else {
                    // Reset cache when snapping is disabled.
                    lastIdleSnapEnabled = false;
                    lastIdleGroundY = null;
                }

                if (Math.abs(camPos1.y - targetY) > 1e-4) {
                    camPos1.y = targetY;

                    // Convert desired camera position -> focal point.
                    focalPoint.copy(forwardVec).mulScalar(distance);
                    focalPoint.sub2(camPos1, focalPoint);
                    camera.setFocalPoint(focalPoint, 0);
                }
            }
        };

        let destroy: () => void = null;

        const wrap = (target: any, name: string, fn: any, options?: any) => {
            const callback = (event: any) => {
                camera.scene.events.fire('camera.controller', name);
                fn(event);
            };
            target.addEventListener(name, callback, options);
            destroy = () => {
                destroy?.();
                target.removeEventListener(name, callback);
            };
        };

        wrap(target, 'pointerdown', pointerdown);
        wrap(target, 'pointerup', pointerup);
        wrap(target, 'pointermove', pointermove);
        wrap(target, 'wheel', wheel, { passive: false });
        wrap(target, 'dblclick', dblclick);
        wrap(window, 'blur', clearAllKeys);
        wrap(window, 'keydown', rawKeyDown);
        wrap(window, 'keyup', rawKeyUp);

        this.destroy = () => {
            destroy?.();
            events.off('camera.fly.forward', onFlyForward);
            events.off('camera.fly.backward', onFlyBackward);
            events.off('camera.fly.left', onFlyLeft);
            events.off('camera.fly.right', onFlyRight);
            events.off('camera.fly.down', onFlyDown);
            events.off('camera.fly.up', onFlyUp);
            events.off('camera.modifier.fast', onModifierFast);
            events.off('camera.modifier.slow', onModifierSlow);
        };
    }
}

export { PointerController };
